import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getGateContext,
  registerAtGate,
  scanGateId,
  type GateContext,
  type GateRegisterResult,
} from "../features/gate/api";
import { FilePicker } from "../components/FilePicker";
import { QrBadge } from "../components/QrBadge";
import { ApiError } from "../lib/api-client";
import { useCompanyBranding } from "../lib/useCompanyBranding";
import { isValidIndianPhone } from "../lib/validation";
import { fileTooLargeError } from "../lib/upload";
import { missingRequiredLabels } from "../lib/visitor-fields";

type FormState = {
  fullName: string;
  hostId: string;
  purpose: string;
  phone: string;
  email: string;
  companyName: string;
  notes: string;
  idType: string;
  idNumber: string;
  hasLaptop: boolean;
  laptopMac: string;
  laptopSerial: string;
};

const INITIAL: FormState = {
  fullName: "",
  hostId: "",
  purpose: "",
  phone: "",
  email: "",
  companyName: "",
  notes: "",
  idType: "AADHAAR",
  idNumber: "",
  hasLaptop: false,
  laptopMac: "",
  laptopSerial: "",
};

export function GateSelfRegisterPage() {
  const { token = "" } = useParams<{ token: string }>();
  const [context, setContext] = useState<GateContext | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [photo, setPhoto] = useState<File | null>(null);
  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<GateRegisterResult | null>(null);

  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const scanInputRef = useRef<HTMLInputElement>(null);

  const { logoUrl } = useCompanyBranding(context?.company_code);

  useEffect(() => {
    getGateContext(token)
      .then(setContext)
      .catch((error: unknown) => {
        setLoadError(
          error instanceof ApiError ? error.message : "This gate QR is invalid or no longer active.",
        );
      });
  }, [token]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function onScanFile(file: File | undefined) {
    if (!file) return;
    const sizeError = fileTooLargeError(file, "ID image");
    if (sizeError) {
      setScanMessage({ text: sizeError, ok: false });
      return;
    }
    // Reuse the scanned image as the uploaded ID document so they don't pick twice.
    setIdDocument(file);
    setScanning(true);
    setScanMessage(null);
    try {
      const r = await scanGateId(token, file);
      setForm((prev) => ({
        ...prev,
        fullName: prev.fullName || r.full_name || prev.fullName,
        idType: r.id_type || prev.idType,
        idNumber: r.id_number || prev.idNumber,
      }));
      setScanMessage({ text: r.validation_message, ok: r.valid });
    } catch (error) {
      setScanMessage({
        text: error instanceof Error ? error.message : "Couldn't scan the ID. Please type your details.",
        ok: false,
      });
    } finally {
      setScanning(false);
    }
  }

  async function submit() {
    if (!form.fullName.trim()) return setSubmitError("Full name is required");
    if (!form.hostId) return setSubmitError("Please choose who you're visiting");
    if (!form.purpose.trim()) return setSubmitError("Please enter the purpose of your visit");

    const missing = missingRequiredLabels(context?.required_fields ?? [], {
      phone: !!form.phone.trim(),
      email: !!form.email.trim(),
      company: !!form.companyName.trim(),
      notes: !!form.notes.trim(),
      id_number: !!form.idNumber.trim(),
      photo: !!photo,
      id_document: !!idDocument,
      laptop: form.hasLaptop && !!form.laptopMac.trim() && !!form.laptopSerial.trim(),
    });
    if (missing.length > 0) return setSubmitError(`Please provide: ${missing.join(", ")}`);

    const sizeError =
      fileTooLargeError(photo, "Your photo") ?? fileTooLargeError(idDocument, "ID document photo");
    if (sizeError) return setSubmitError(sizeError);

    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await registerAtGate(token, {
        fullName: form.fullName.trim(),
        hostId: Number(form.hostId),
        purpose: form.purpose.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        companyName: form.companyName.trim(),
        notes: form.notes.trim(),
        idType: form.idType,
        idNumber: form.idNumber.trim(),
        hasLaptop: form.hasLaptop,
        laptopMac: form.laptopMac.trim(),
        laptopSerial: form.laptopSerial.trim(),
        photo,
        idDocument,
      });
      setResult(res);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Could not register your visit");
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <main className="invite-shell">
        <section className="hero">
          <h1>Gate QR unavailable</h1>
          <p className="subtitle">{loadError}</p>
        </section>
      </main>
    );
  }

  if (!context) {
    return (
      <main className="invite-shell">
        <section className="hero">
          <h1>Loading…</h1>
        </section>
      </main>
    );
  }

  if (result) {
    return (
      <main className="invite-shell">
        <section className="hero">
          <h1>You're checked in — pending approval</h1>
          <p className="subtitle">
            Thanks, {result.visitor_name}. Show this pass to security. Your host will approve your
            visit shortly.
          </p>
        </section>
        <section className="panel" style={{ textAlign: "center" }}>
          <QrBadge badgeCode={result.badge_code} />
          <p className="management-note">
            <Link to={`/badge/${encodeURIComponent(result.badge_code)}`}>Open my full pass</Link>
          </p>
        </section>
      </main>
    );
  }

  const required = context.required_fields ?? [];
  const star = (key: string) => (required.includes(key) ? <span className="req">*</span> : null);

  return (
    <main className="invite-shell">
      <section className="hero">
        <p className="eyebrow">
          {logoUrl ? (
            <img src={logoUrl} alt={context.company_code} className="company-logo" />
          ) : (
            <span className="brand-wordmark">
              0todeploy <span className="brand-vms">VMS</span>
            </span>
          )}
          <span className="role-chip">Visitor Check-In</span>
        </p>
        <h1>Welcome to {context.company_name}</h1>
        <p className="subtitle">Fill in your details to register your visit.</p>
      </section>

      <form
        className="panel vform"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="vform-scan">
          <h3>📷 Scan your ID to auto-fill</h3>
          <p>Snap a photo of your Aadhaar / PAN / passport and we'll fill in your name and ID for you.</p>
          <p className="vform-scan-hint">💡 Hold the ID flat, fill the frame, and avoid glare and shadows for the best read.</p>
          <button
            type="button"
            className="vform-scan-btn"
            onClick={() => scanInputRef.current?.click()}
            disabled={scanning}
          >
            {scanning ? "Reading your ID…" : "Scan ID"}
          </button>
          <input
            ref={scanInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            style={{ display: "none" }}
            onChange={(event) => void onScanFile(event.target.files?.[0] ?? undefined)}
          />
          {scanMessage ? (
            <div className={`vform-scan-result ${scanMessage.ok ? "ok" : "warn"}`}>
              {scanMessage.ok ? "✓ " : "⚠ "}
              {scanMessage.text}
            </div>
          ) : null}
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-name">
            Full name<span className="req">*</span>
          </label>
          <input id="v-name" value={form.fullName} onChange={(e) => set("fullName", e.target.value)} placeholder="Your full name" />
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-host">
            Who are you visiting?<span className="req">*</span>
          </label>
          <select id="v-host" value={form.hostId} onChange={(e) => set("hostId", e.target.value)}>
            <option value="">Select a host…</option>
            {context.hosts.map((host) => (
              <option key={host.id} value={String(host.id)}>
                {host.name}
                {host.department ? ` — ${host.department}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-purpose">
            Purpose of visit<span className="req">*</span>
          </label>
          <input id="v-purpose" value={form.purpose} onChange={(e) => set("purpose", e.target.value)} placeholder="e.g. Interview, Meeting, Delivery" />
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-phone">
            Phone {star("phone")}
          </label>
          <input id="v-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="10-digit mobile" inputMode="numeric" />
          {form.phone.trim() && !isValidIndianPhone(form.phone) ? (
            <span className="vform-hint">Enter a valid 10-digit mobile number</span>
          ) : null}
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-email">
            Email {star("email")}
          </label>
          <input id="v-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" />
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-company">
            Company {star("company")}
          </label>
          <input id="v-company" value={form.companyName} onChange={(e) => set("companyName", e.target.value)} placeholder="Your company (optional)" />
        </div>

        <div className="vform-row">
          <div className="vform-field">
            <label className="vform-label" htmlFor="v-idtype">
              ID type
            </label>
            <select id="v-idtype" value={form.idType} onChange={(e) => set("idType", e.target.value)}>
              <option value="AADHAAR">Aadhaar</option>
              <option value="PAN">PAN</option>
              <option value="PASSPORT">Passport</option>
              <option value="DRIVING_LICENSE">Driving License</option>
              <option value="OTHER">Other ID</option>
            </select>
          </div>
          <div className="vform-field">
            <label className="vform-label" htmlFor="v-idnum">
              ID number {star("id_number")}
            </label>
            <input id="v-idnum" value={form.idNumber} onChange={(e) => set("idNumber", e.target.value)} placeholder="Kept confidential" autoComplete="off" />
          </div>
        </div>

        <div className="vform-field">
          <label className="vform-label" htmlFor="v-notes">
            Notes {star("notes")}
          </label>
          <textarea id="v-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything else we should know?" rows={3} />
        </div>

        <FilePicker label={`Your photo${required.includes("photo") ? " *" : ""}`} file={photo} onChange={setPhoto} />
        <FilePicker label={`ID document photo${required.includes("id_document") ? " *" : ""}`} file={idDocument} onChange={setIdDocument} />

        <label className="vform-check">
          <input type="checkbox" checked={form.hasLaptop} onChange={(e) => set("hasLaptop", e.target.checked)} />
          I'm bringing a laptop
        </label>
        {form.hasLaptop ? (
          <div className="vform-row">
            <div className="vform-field">
              <label className="vform-label">Laptop MAC</label>
              <input value={form.laptopMac} onChange={(e) => set("laptopMac", e.target.value)} placeholder="MAC address" />
            </div>
            <div className="vform-field">
              <label className="vform-label">Laptop serial</label>
              <input value={form.laptopSerial} onChange={(e) => set("laptopSerial", e.target.value)} placeholder="Serial number" />
            </div>
          </div>
        ) : null}

        {submitError ? <p className="error-banner">{submitError}</p> : null}

        <button className="vform-submit" type="submit" disabled={submitting}>
          {submitting ? "Submitting…" : "Create my pass"}
        </button>
      </form>
    </main>
  );
}
